import axios from 'axios'

/**
 * True only for errors that mean "this stage is really gone, or this user may no
 * longer see it" — i.e. the two cases where dropping the persisted
 * `activeProjectStage` is the correct repair.
 *
 * Everything else must return false. A transient gateway timeout, a 5xx during a
 * deploy, or a flaky mobile connection says nothing about whether the stage
 * still exists, and clearing the active stage on those destroys a server-side
 * selection the user did not ask to lose: the next load has no active stage,
 * which reads as being thrown out of the app.
 *
 * 401 is deliberately NOT here: the axios interceptor already redirects to
 * /login for it, so the session — not the stage — is what needs repairing.
 * `api/src/Security/EntryPoint/ApiAuthenticationEntryPoint.php` converts
 * authorization refusals under remember-me back to 403, so genuine access loss
 * does arrive as 403 rather than 401.
 *
 * Keyed off `isAxiosError` rather than `instanceof AxiosError` so a second copy
 * of axios in the bundle, or an error that crossed a realm, cannot silently make
 * every failure unclassifiable.
 */
export const isStageAccessRevokedError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false
  const status = error.response?.status
  return status === 403 || status === 404
}

/** `9`, `"9"` and `"/project_stages/9"` all normalize to `"9"`. */
const normalizeStageId = (value: string | number | null | undefined): string | null => {
  if (value === null || value === undefined) return null
  const text = String(value)
  if (text === '') return null
  return text.split('/').filter(Boolean).pop() ?? null
}

/**
 * Whether a failed stage fetch justifies clearing the user's persisted
 * `activeProjectStage`.
 *
 * Three conditions, all required:
 *  - the user actually has a stage persisted (otherwise the PATCH is a no-op
 *    that still invalidates ['user'] and resets in-progress form state),
 *  - at least one of the failures says the stage is gone / forbidden,
 *  - the stage that failed IS the persisted one. A route param overrides the
 *    persisted stage (`rootLoader`), so following a stale deep link to someone
 *    else's stage used to wipe the perfectly valid stage the user had selected.
 */
export const shouldClearActiveProjectStage = ({
  persistedActiveStage,
  failedStageId,
  errors,
}: {
  persistedActiveStage: string | null | undefined
  failedStageId: string | number | null | undefined
  errors: unknown[]
}): boolean => {
  const persisted = normalizeStageId(persistedActiveStage)
  if (!persisted) return false
  if (normalizeStageId(failedStageId) !== persisted) return false
  return errors.some(isStageAccessRevokedError)
}
